import React, { useState, useEffect } from 'react';
import { Product } from '../types';
import { supabase, isSupabaseConfigured } from '../lib/supabaseClient';
import ProductCarousel from './ProductCarousel';

type PopularRow = {
  id: string;
  name: string;
  slug?: string | null;
  category: string;
  price_per_day: number;
  price_on_request?: boolean | null;
  short_description?: string | null;
  image?: string | null;
  popular?: boolean | null;
  featured?: boolean | null;
};

const FeaturedProducts: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void fetchPopularNow();
  }, []);

  const mapRowToProduct = (product: PopularRow): Product => ({
    id: product.id,
    name: product.name,
    category: product.category,
    pricePerDay: Number(product.price_per_day) || 0,
    priceOnRequest: Boolean(product.price_on_request),
    description: '',
    shortDescription: product.short_description || '',
    image: product.image || '',
    gallery: [],
    popular: Boolean(product.popular),
    featured: Boolean(product.featured),
    includes: [],
    upgrades: [],
    slug: product.slug || undefined,
  });

  const fetchPackageFallback = async (): Promise<Product[]> => {
    const { data: fallbackRows, error: fallbackErr } = await supabase
      .from('products')
      .select(
        'id,name,slug,category,price_per_day,price_on_request,short_description,image,popular,featured'
      )
      .eq('hidden_from_products', false)
      .or('category.eq.Paket,is_package.eq.true')
      .or('featured.eq.true,popular.eq.true')
      .order('display_order', { ascending: true })
      .limit(6);
    if (fallbackErr) throw fallbackErr;
    return (fallbackRows || []).filter((row) => row?.id).map((row) => mapRowToProduct(row as PopularRow));
  };

  const fetchPopularNow = async () => {
    try {
      if (!isSupabaseConfigured()) {
        throw new Error('Supabase är inte konfigurerad. Kontrollera dina miljövariabler.');
      }

      const { data, error: rpcError } = await supabase.rpc('get_public_popular_products', {
        p_limit: 6,
      });

      let mapped: Product[] = [];

      if (rpcError) {
        console.warn('Popular products RPC failed, using direct product query:', rpcError);
        mapped = await fetchPackageFallback();
      } else {
        const rows = (Array.isArray(data) ? data : []) as PopularRow[];
        mapped = rows.filter((r) => r?.id).map(mapRowToProduct);

        // Client-side pad if RPC returned fewer than 3 (packages only)
        if (mapped.length < 3) {
          const seen = new Set(mapped.map((p) => p.id));
          for (const product of await fetchPackageFallback()) {
            if (seen.has(product.id)) continue;
            seen.add(product.id);
            mapped.push(product);
          }
        }
      }

      setProducts(mapped.slice(0, 6));
      setError(null);
    } catch (err: unknown) {
      console.error('Error fetching popular products:', err);
      setError(err instanceof Error ? err.message : 'Ett fel uppstod när produkterna skulle hämtas');
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div id="populara-paket">
      <ProductCarousel
        products={products}
        title="Populärt just nu"
        loading={loading}
        error={error}
        sectionClassName="py-12 md:py-16 border-t border-slate-800"
        cardCtaMode="homePopular"
        showAllInView={products.length > 0 && products.length <= 3}
      />
    </div>
  );
};

export default FeaturedProducts;
